/**
 * WaCloudSafetyBanner — Phase 9.1 4-state operational banner.
 *
 * Drives off `active_channel` from /api/admin/wa-cloud/status:
 *   - dry_run → amber  (no Meta calls at all)
 *   - canary  → blue   (limited to whitelisted test phones)
 *   - live    → green  (production traffic active)
 *   - blocked → red    (enabled=true but live_sending_block_reason set)
 *
 * Read-only; this banner never toggles configuration.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  WarningCircle, CheckCircle, Info, X as XIcon, Flask,
} from "@phosphor-icons/react";

const MODE_DEF = {
  dry_run: {
    tone: { box: "border-amber-500/30 bg-amber-500/10", text: "text-amber-200", icon: "text-amber-400" },
    Icon: Info,
    title: "WhatsApp Cloud API · Dry-run mode",
    msg: "WHATSAPP_CLOUD_API_ENABLED=false. No live Meta calls. All sends are stored as dry-run records.",
    dismissible: true,
  },
  canary: {
    tone: { box: "border-sky-500/40 bg-sky-500/10", text: "text-sky-200", icon: "text-sky-400" },
    Icon: Flask,
    title: "WhatsApp Cloud API · Canary mode",
    msg: "Only whitelisted test phones can receive live sends. All other recipients are blocked server-side.",
    dismissible: false,
  },
  live: {
    tone: { box: "border-emerald-500/30 bg-emerald-500/10", text: "text-emerald-300", icon: "text-emerald-400" },
    Icon: CheckCircle,
    title: "WhatsApp Cloud API · Live",
    msg: "Production traffic is active. Every send is audited.",
    dismissible: true,
  },
  blocked: {
    tone: { box: "border-red-500/40 bg-red-500/10", text: "text-red-300", icon: "text-red-400" },
    Icon: WarningCircle,
    title: "WhatsApp Cloud API · Blocked",
    msg: "Live sending is enabled but production safety configuration is missing.",
    dismissible: false,
  },
};

export default function WaCloudSafetyBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/wa-cloud/status")
      .then(({ data }) => { if (mounted) setStatus(data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!status || dismissed) return null;

  const mode = status.active_channel || (status.enabled ? "live" : "dry_run");
  const def = MODE_DEF[mode] || MODE_DEF.dry_run;

  const reason = mode === "blocked" ? (status.live_sending_block_reason || "Missing production configuration.") : def.msg;

  return (
    <div
      data-testid="wa-cloud-safety-banner"
      data-state={mode}
      className={`flex items-start gap-3 px-3 py-2.5 border ${def.tone.box}`}
    >
      <def.Icon size={16} weight="bold" className={`${def.tone.icon} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className={`text-[11px] font-display tracking-widest uppercase font-bold ${def.tone.text}`}>
          {def.title}
        </div>
        <div className="text-white/70 text-xs mt-0.5">{reason}</div>
        {mode === "canary" && status.canary_phones_count > 0 && (
          <div className="text-[10px] text-sky-200/70 font-mono-data mt-0.5">
            Whitelist: {status.canary_phones_preview?.join(", ") || `${status.canary_phones_count} phones`}
          </div>
        )}
      </div>
      <Link
        to="/admin/wa-cloud"
        data-testid="wa-cloud-banner-details-link"
        className="hidden sm:inline-flex text-[10px] font-display tracking-widest uppercase font-bold text-white/65 hover:text-white border border-white/20 hover:border-white/50 px-2 py-1 mt-0.5"
      >
        Details
      </Link>
      <span className="text-[10px] text-white/45 font-mono-data whitespace-nowrap mt-1 hidden md:inline">
        env={status.app_env} · {mode}
      </span>
      {def.dismissible && (
        <button
          data-testid="wa-cloud-banner-dismiss"
          onClick={() => setDismissed(true)}
          className="text-white/45 hover:text-white mt-0.5"
          aria-label="Dismiss"
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );
}
